import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { ApiError, changePasswordRequest } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';

const schema = z
  .object({
    currentPassword: z.string().min(1, 'errors.validation.passwordRequired'),
    newPassword: z.string().min(8, 'errors.validation.passwordTooShort'),
    confirm: z.string().min(1, 'errors.validation.passwordRequired'),
  })
  .refine((data) => data.newPassword === data.confirm, {
    path: ['confirm'],
    message: 'changePassword.passwordMismatch',
  });

type FormValues = z.infer<typeof schema>;

/**
 * Password change screen (#78). Reachable two ways:
 * - forced: temp-password login sets mustChangePassword → AuthGuard/login
 *   redirect here and the rest of the API is blocked server-side;
 * - voluntary: sidebar account menu.
 * Requires an authenticated session (server enforces it on POST), but lives
 * OUTSIDE the AppLayout guard so the redirect cannot loop.
 */
export default function ChangePasswordPage() {
  const { isAuthenticated, isLoading, user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  if (isLoading) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-background p-8">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-32" />
      </div>
    );
  }

  // Not signed in → back to login (the form itself is session-gated).
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const onSubmit = async (data: FormValues) => {
    clearErrors('root');
    try {
      await changePasswordRequest(data.currentPassword, data.newPassword);
      toast.success(t('changePassword.successToast'));
      // The server cleared mustChangePassword — re-fetch the operator so the
      // AuthGuard releases the app on the next navigation.
      await refreshUser();
      navigate('/', { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setError('root', { message: t('changePassword.badCurrentPassword') });
      } else if (error instanceof ApiError && error.status === 403) {
        setError('root', { message: t('changePassword.forbidden') });
      } else {
        toast.error(t('changePassword.unreachable'));
      }
    }
  };

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-background p-4">
      {/* Quiet tonal field: a barely-there radial lift of the graphite */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 90% at 50% 0%, hsl(var(--accent) / 0.55) 0%, hsl(var(--background)) 62%)',
        }}
      />

      <div className="relative w-full max-w-[400px] motion-safe:duration-500 motion-safe:animate-in motion-safe:fade-in">
        <div className="rounded-[10px] border bg-card px-6 py-8 shadow-[0_1px_0_0_hsl(var(--foreground)/0.03),0_16px_48px_-16px_hsl(33_18%_4%/0.55)] sm:px-9 sm:py-10">
          {/* Wordmark + env */}
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2.5">
              <span className="h-2.5 w-2.5 rounded-[3px] bg-primary" aria-hidden="true" />
              <span className="text-[19px] font-semibold tracking-tight text-foreground">
                License
              </span>
            </span>
            <span className="text-xs text-faint">{user?.username}</span>
          </div>

          <h1 className="mt-9 text-[26px] font-semibold leading-tight tracking-tight text-foreground">
            {t('changePassword.title')}
          </h1>
          <p className="mt-2 font-serif text-[16px] italic leading-snug text-muted-foreground">
            {t('changePassword.subtitle')}
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5">
            {errors.root && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-[13px] text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{errors.root.message}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="currentPassword">{t('changePassword.currentPassword')}</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrent ? 'text' : 'password'}
                  autoComplete="current-password"
                  disabled={isSubmitting}
                  autoFocus
                  className="h-11 pr-10"
                  {...register('currentPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-faint hover:text-foreground"
                  aria-label={t('changePassword.togglePassword')}
                >
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.currentPassword && (
                <p className="text-[12.5px] text-destructive">
                  {t(String(errors.currentPassword.message))}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">{t('changePassword.newPassword')}</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNew ? 'text' : 'password'}
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  className="h-11 pr-10"
                  {...register('newPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-faint hover:text-foreground"
                  aria-label={t('changePassword.togglePassword')}
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-[12.5px] text-destructive">
                  {t(String(errors.newPassword.message))}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm">{t('changePassword.confirm')}</Label>
              <div className="relative">
                <Input
                  id="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  className="h-11 pr-10"
                  {...register('confirm')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-faint hover:text-foreground"
                  aria-label={t('changePassword.togglePassword')}
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirm && (
                <p className="text-[12.5px] text-destructive">
                  {t(String(errors.confirm.message))}
                </p>
              )}
            </div>

            <Button type="submit" className="h-11 w-full" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {t('changePassword.submit')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
